/**
 * Form building blocks for the WorkWise parts of the New Deployment wizard.
 * They use ONLY the classes of NewDeploymentPage.module.css, so WorkWise looks
 * exactly like the rest of the page. Each reads the WorkWise form context, so a
 * section only names the field path.
 */
import type { ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'
import { Controller, type Path, type RegisterOptions, useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { WorkwiseFormValues } from '@/types/workwise'
import { errorAt, fieldId } from './fieldHelpers'

type FormPath = Path<WorkwiseFormValues>

// ─── Section ─────────────────────────────────────────────────────────────────

interface SectionProps {
  title: string
  /** A pill on the right of the title (e.g. the locked "Dedicated"). */
  badge?: string
  testId?: string
  children: ReactNode
}

/** A titled block in the networkSection style used by the Environment and Region sections. */
export function Section({ title, badge, testId, children }: SectionProps) {
  return (
    <div className={styles.networkSection} data-testid={testId}>
      {badge ? (
        <div className={styles.sectionHeader}>
          <h4 className={styles.networkSectionTitle} style={{ margin: 0 }}>{title}</h4>
          <span className={styles.selectedBadge} data-testid={testId ? `${testId}-badge` : undefined}>{badge}</span>
        </div>
      ) : (
        <h4 className={styles.networkSectionTitle}>{title}</h4>
      )}
      {children}
    </div>
  )
}

// ─── Field wrapper ───────────────────────────────────────────────────────────

interface FieldProps {
  path: string
  label: string
  hint?: ReactNode
  required?: boolean
  /** Rendered at the right of the label, like the "+ Add Disk" button. */
  action?: ReactNode
  children: ReactNode
}

export function Field({ path, label, hint, required, action, children }: FieldProps) {
  const {
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const error = errorAt(errors, path)
  return (
    <div className={styles.formGroup}>
      <label
        className={styles.label}
        htmlFor={fieldId(path)}
        style={action ? { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } : undefined}
      >
        <span>
          {label}
          {required && <span className={styles.required}> *</span>}
        </span>
        {action}
      </label>
      {children}
      {hint && <div className={styles.fieldHint}>{hint}</div>}
      {error && (
        <span className={styles.errorMsg} role="alert" data-testid={`error-${path}`}>
          {error}
        </span>
      )}
    </div>
  )
}

// ─── Text / number / date input ──────────────────────────────────────────────

interface TextFieldProps {
  path: string
  label: string
  hint?: ReactNode
  required?: boolean
  action?: ReactNode
  type?: 'text' | 'number' | 'date'
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  inputMode?: 'numeric' | 'text' | 'email'
  /** Parse as a required number (NaN when empty). */
  number?: boolean
  /** Parse as an optional number (undefined when empty). */
  optionalNumber?: boolean
  min?: number
  max?: number
  /** Overrides the default `field-<path>` test id. */
  testId?: string
}

export function TextField({
  path,
  label,
  hint,
  required,
  action,
  type = 'text',
  placeholder,
  disabled,
  maxLength,
  inputMode,
  number,
  optionalNumber,
  min,
  max,
  testId,
}: TextFieldProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const error = errorAt(errors, path)
  let options: RegisterOptions<WorkwiseFormValues> | undefined
  if (number) options = { valueAsNumber: true } as RegisterOptions<WorkwiseFormValues>
  else if (optionalNumber) {
    options = { setValueAs: (v: string) => (v === '' || v == null ? undefined : Number(v)) } as RegisterOptions<WorkwiseFormValues>
  }
  return (
    <Field path={path} label={label} hint={hint} required={required} action={action}>
      <input
        id={fieldId(path)}
        data-testid={testId ?? `field-${path}`}
        className={clsx(styles.input, error && styles.inputError)}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        inputMode={inputMode}
        min={min}
        max={max}
        autoComplete="off"
        spellCheck={false}
        aria-invalid={error ? true : undefined}
        {...register(path as FormPath, options)}
      />
    </Field>
  )
}

// ─── Select ──────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface SelectFieldProps {
  path: string
  label: string
  options: SelectOption[]
  hint?: ReactNode
  required?: boolean
  disabled?: boolean
  /** Parse the chosen value as a number (undefined for the empty option). */
  asNumber?: boolean
  /** Overrides the default `field-<path>` test id. */
  testId?: string
  /** Called with the chosen value after the form has taken it. */
  onChange?: (value: string) => void
}

/** Controlled, so the shown option follows the form value even when options load later. */
export function SelectField({ path, label, options, hint, required, disabled, asNumber, testId, onChange }: SelectFieldProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const error = errorAt(errors, path)
  return (
    <Field path={path} label={label} hint={hint} required={required}>
      <Controller
        control={control}
        name={path as FormPath}
        render={({ field }) => (
          <select
            id={fieldId(path)}
            data-testid={testId ?? `field-${path}`}
            className={clsx(styles.select, error && styles.inputError)}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            value={field.value == null ? '' : String(field.value)}
            onBlur={field.onBlur}
            onChange={(e) => {
              field.onChange(asNumber ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)
              onChange?.(e.target.value)
            }}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      />
    </Field>
  )
}

// ─── Radio group in the CI/CD style ──────────────────────────────────────────

interface RadioChoice {
  value: string
  label: string
  icon?: ReactNode
  disabled?: boolean
}

interface RadioFieldProps {
  path: string
  label: string
  choices: RadioChoice[]
  /** Prefix for the per-choice test ids, e.g. "db-mode" -> "db-mode-shared". */
  testIdPrefix: string
  hint?: ReactNode
  onChange?: (value: string) => void
}

/** The radioGroup / radioOption look of the CI/CD Pipeline choice. */
export function RadioField({ path, label, choices, testIdPrefix, hint, onChange }: RadioFieldProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<WorkwiseFormValues>()
  const error = errorAt(errors, path)
  return (
    <Controller
      control={control}
      name={path as FormPath}
      render={({ field }) => (
        <div className={styles.formGroup}>
          <label className={styles.label}>{label}</label>
          <div className={styles.radioGroup} role="radiogroup" aria-label={label}>
            {choices.map((choice) => (
              <label
                key={choice.value}
                className={clsx(styles.radioOption, field.value === choice.value && styles.radioOptionSelected)}
                style={choice.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
              >
                <input
                  type="radio"
                  name={path}
                  value={choice.value}
                  checked={field.value === choice.value}
                  disabled={choice.disabled}
                  aria-invalid={error ? true : undefined}
                  data-testid={`${testIdPrefix}-${choice.value}`}
                  onChange={() => {
                    field.onChange(choice.value)
                    onChange?.(choice.value)
                  }}
                />
                {choice.icon}
                {choice.label}
              </label>
            ))}
          </div>
          {hint && <div className={styles.fieldHint}>{hint}</div>}
          {error && (
            <span className={styles.errorMsg} role="alert" data-testid={`error-${path}`}>
              {error}
            </span>
          )}
        </div>
      )}
    />
  )
}

// ─── Toggle row ──────────────────────────────────────────────────────────────

interface ToggleFieldProps {
  path: string
  label: string
  desc?: string
}

/** The toggleRow + switch look of "Create Elastic Pool" / "Claude AI Auto-Fix". */
export function ToggleField({ path, label, desc }: ToggleFieldProps) {
  const { control } = useFormContext<WorkwiseFormValues>()
  return (
    <Controller
      control={control}
      name={path as FormPath}
      render={({ field }) => (
        <div className={styles.toggleRow}>
          <div>
            <span className={styles.toggleLabel}>{label}</span>
            {desc && <span className={styles.toggleDesc}>{desc}</span>}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={Boolean(field.value)}
            aria-label={label}
            data-testid={`field-${path}`}
            className={clsx(styles.toggle, Boolean(field.value) && styles.toggleOn)}
            onClick={() => field.onChange(!field.value)}
          >
            <span className={styles.toggleThumb} />
          </button>
        </div>
      )}
    />
  )
}

// ─── Read-only value ─────────────────────────────────────────────────────────

interface InfoFieldProps {
  label: string
  value: ReactNode
  hint?: ReactNode
  testId?: string
  /** Shown muted instead of an empty value (like the Ross "Not mapped for this region"). */
  empty?: ReactNode
  /** A server-side error for this read-only value (e.g. the backend refusing the SQL image), shown under it. */
  error?: string
  errorTestId?: string
}

/** A read-only value in the vnetInfoBox look (e.g. the dedicated DB's SQL Server image). */
export function InfoField({ label, value, hint, testId, empty, error, errorTestId }: InfoFieldProps) {
  const blank = empty !== undefined && (value === '' || value === null || value === undefined)
  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.vnetInfoBox}>
        {blank ? (
          <span className={styles.vnetHint} data-testid={testId} data-empty="true">{empty}</span>
        ) : (
          <span className={styles.vnetName} data-testid={testId}>{value}</span>
        )}
        {hint && <span className={styles.vnetHint}>{hint}</span>}
      </div>
      {error && (
        <span className={styles.errorMsg} role="alert" data-testid={errorTestId}>
          {error}
        </span>
      )}
    </div>
  )
}

// ─── Error list ──────────────────────────────────────────────────────────────

/** Server-side errors in the deployError look used on the Review step. */
export function ErrorList({ title, messages, testId }: { title: string; messages: string[]; testId?: string }) {
  if (messages.length === 0) return null
  return (
    <div className={styles.deployError} role="alert" data-testid={testId} style={{ marginTop: 0, alignItems: 'flex-start' }}>
      <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <strong>{title}</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {messages.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ─── Review-style row ────────────────────────────────────────────────────────

/** The reviewRow look of the page's ReviewRow (label left, value right). */
export function Row({ label, value, mono, testId }: { label: string; value: ReactNode; mono?: boolean; testId?: string }) {
  return (
    <div className={styles.reviewRow}>
      <span className={styles.reviewLabel}>{label}</span>
      <span className={clsx(styles.reviewValue, mono && styles.reviewValueMono)} data-testid={testId}>
        {value}
      </span>
    </div>
  )
}
